import { useRouter } from 'next/router';
import React from 'react';

import type { GetServerSideProps, NextPage } from 'next';

import { connect } from 'react-redux';

import LoginForm from '@/components/auth/LoginForm';
import { AmbaAlert } from '@/components/global/AmbaAlert';
import AmbaImage from '@/components/global/AmbaImage';
import ConfirmationModal from '@/components/global/modal/ConfirmationModal';
import { useModals } from '@/components/global/modal/ModalManagerProvider';
import { Shown } from '@/components/global/Shown';
import { readCookie } from '@/components/table/DataTable/util';
import { getUrlWithHost } from '@/helpers/url';
import useLoginColors from '@/hooks/useLoginColors';
import { useTranslation } from '@/i18n';
import { Channel } from '@/models/channelModels';
import {
  doLogin,
  doResetLoginStatus,
  getCompanyAccount,
  getPortalHost,
  handleAccountActivation,
  setPortalHost,
} from '@/redux/auth/actions';
import { doCustomizeLogin } from '@/redux/customizations/actions';
import { selectLoginCustomization, selectPortalCustomizationsCustomization } from '@/redux/customizations/reducers';
import { RootState } from '@/redux/rootReducer';
import { envVars } from '@/services/ApiService';
import LocalDataService from '@/services/LocalDataService';
import { getCommonServerSideProps } from '@/util/getServerSideProps';
import LayoutConsume from '@/wrappers/layout/LayoutConsume';

import { AuthPropTypes, LoginFormType } from '../models/authModels';
import { AmbaAlertTypes, ServerStatus } from '../models/globalModels';

const mapDispatchToProps = {
  doLogin,
  getCompanyAccount,
  setPortalHost,
  getPortalHost,
  doCustomizeLogin,
  doResetLoginStatus,
  handleAccountActivation,
};

const mapStateToProps = (state: RootState) => {
  const auth = state.auth;
  const customizations = state.customizations;
  return {
    loginStatus: auth.loginStatus,
    userStatus: auth.userStatus,
    ambassadorStatus: auth.ambassadorStatus,
    companyStatus: auth.companyStatus,
    loginStatusMessage: auth.loginStatusMessage,
    authValidationResponse: auth.authValidationResponse,
    companyAccount: auth.companyAccount,
    customization: selectLoginCustomization(state),
    customizationStatus: customizations.customizationStatus,
    isAccountActive: auth.isAccountActive,
    user: auth.user,
    portalCustomizations: selectPortalCustomizationsCustomization(state),
  };
};

const Login: NextPage<AuthPropTypes> = ({
  loginStatus,
  ambassadorStatus,
  companyAccount,
  companyStatus,
  doLogin,
  doResetLoginStatus,
  handleAccountActivation,
  getCompanyAccount,
  setPortalHost,
  customization,
  customizationStatus,
  doCustomizeLogin,
  authValidationResponse,
  isAccountActive,
  user,
  portalCustomizations,
  nonce,
}) => {
  const [loginValidationMsg, setloginValidationMsg] = React.useState<string>('');
  const uid = React.useRef(0);
  const router = useRouter();
  const { openModal } = useModals();
  const { t } = useTranslation('auth');

  useLoginColors();

  const isAuthenticated = () => {
    return Boolean(LocalDataService.getAuth() || LocalDataService.getUserId() || LocalDataService.getUid());
  };

  const isStorefront = React.useMemo(() => {
    try {
      return readCookie('portal_channel') === Channel.STOREFRONT;
    } catch {
      return false;
    }
  }, []);

  // redirects to dashboard when user is authenticated and has active acct
  React.useEffect(() => {
    if (ambassadorStatus == ServerStatus.FETCH && isAccountActive) {
      try {
        document.cookie = `portal_channel=${Channel.DEFAULT}; Path=/; SameSite=Lax`;
      } catch {}
      window.location.href = getUrlWithHost('/dashboard', envVars.checkHost);
    } else if (!isAccountActive) {
      // triggers confirmation modal to reactivate acct
      uid.current = user.uid;
      openModal(ConfirmationModal, {
        title: 'Account Reactivation',
        message: t('messages.reactivateAccount'),
        onConfirm: reactivateAccount,
        size: 'sm',
      });
    }
  }, [ambassadorStatus, router, isAccountActive, user.uid]);

  // sets the customization ready for login page
  React.useEffect(() => {
    if (customizationStatus == ServerStatus.FETCH && doCustomizeLogin) {
      doCustomizeLogin(customization);
    }
  }, [customizationStatus]);

  // triggers an UI AmbaAlert with the response from the API
  React.useEffect(() => {
    if (authValidationResponse?.non_field_errors) {
      setloginValidationMsg(authValidationResponse.non_field_errors[0]);
    }
  }, [authValidationResponse]);

  React.useEffect(() => {
    if (isStorefront && isAuthenticated()) {
      window.location.href = getUrlWithHost('/app/rewards/v3', envVars.checkHost);
    }
  }, [isStorefront]);

  // Only sets default channel cookie if we are NOT in storefront
  React.useEffect(() => {
    if (!isStorefront) {
      try {
        document.cookie = `portal_channel=${Channel.DEFAULT}; Path=/; SameSite=Lax`;
      } catch {}
    }
  }, [isStorefront]);

  const handleLogin = (form: LoginFormType) => {
    setloginValidationMsg('');
    if (doLogin) {
      doLogin(form.email, form.password, getPortalHost());
    }
  };

  const reactivateAccount = () => {
    if (handleAccountActivation && uid.current > 0) {
      handleAccountActivation(uid.current, true);
    }
  };

  return (
    <LayoutConsume menuActive={false}>
      <div className="LoginPage">
        <div className="h-100 d-flex align-items-center justify-content-center">
          <Shown extraClass="loginBg">
            <div className="loginFormWrapper p-5">
              <AmbaAlert
                alertMessage={loginValidationMsg}
                isDismiss={true}
                showAlert={loginValidationMsg ? true : false}
                type={AmbaAlertTypes.ERROR}
              />
              <div className="row companyLogo p-1">
                <AmbaImage
                  alt={companyAccount?.company_name as string}
                  fallbackSrc="/images/branding-logo.png"
                  imageSrc={customization?.login_page_logo as string}
                  isLink={false}
                />
              </div>
              <LoginForm
                isLoading={ambassadorStatus == ServerStatus.FETCHING}
                onSubmit={handleLogin}
                validationResponse={authValidationResponse}
              />
            </div>
          </Shown>
        </div>
      </div>
    </LayoutConsume>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Login);

export const getServerSideProps: GetServerSideProps = async (context) => {
  const commonProps = await getCommonServerSideProps(context);
  return {
    props: {
      ...commonProps,
    },
  };
};
